import { useState } from 'react';
import { Trash, Edit, Clock, ChevronDown } from 'react-feather';
import Button from '@/UIlibrary/Button/Button';
import { TextBadge } from '@/UIlibrary/TextBadge/TextBadge';
import { ToolInstructionWithTools } from '@/pages/CustomerSupport/types';
import { useTranslations } from 'use-intl';
import { VersionHistoryPanel } from '@/components/VersionHistoryPanel';

function truncateWithEllipsis(text: string, maxLength: number) {
	if (text.length <= maxLength) return text;
	return `${text.slice(0, maxLength).trim()}…`;
}

interface ToolInstructionListProps {
	instructions: ToolInstructionWithTools[];
	onEdit: (instruction: ToolInstructionWithTools) => void;
	onDelete: (id: string) => void;
}

export function ToolInstructionList({ instructions, onEdit, onDelete }: ToolInstructionListProps) {
	const t = useTranslations('ManageAgents');
	const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);

	if (instructions.length === 0) {
		return (
			<div className="py-10 text-center text-gray-500 text-sm">{t('NoInstructionsConfigured')}</div>
		);
	}

	return (
		<div className="space-y-2">
			{instructions.map((instruction) => (
				<div key={instruction.id} className="group">
					<div
						className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 transition-all hover:border-gray-300 hover:shadow-sm"
						role="button"
						tabIndex={0}
						onClick={() => onEdit(instruction)}
						onKeyDown={(e) => e.key === 'Enter' && onEdit(instruction)}
					>
						<div className="flex-1 min-w-0">
							<div className="text-sm text-gray-800 line-clamp-2">
								{truncateWithEllipsis(instruction.trigger_description, 120)}
							</div>
							<div className="flex flex-wrap gap-1.5 mt-2">
								{instruction.tools.map((tool) => (
									<TextBadge key={tool.id} text={tool.name} type="primary" size="sm" />
								))}
							</div>
						</div>
						<div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
							<Button
								color="gray"
								size="auto"
								textOnly
								onClick={(e) => {
									e.stopPropagation();
									setExpandedHistoryId(
										expandedHistoryId === instruction.id ? null : instruction.id
									);
								}}
								startIcon={
									expandedHistoryId === instruction.id ? (
										<ChevronDown className="h-4 w-4" />
									) : (
										<Clock className="h-4 w-4" />
									)
								}
								label=""
								title={t('VersionHistory')}
							/>
							<Button
								color="gray"
								size="auto"
								textOnly
								onClick={(e) => {
									e.stopPropagation();
									onEdit(instruction);
								}}
								startIcon={<Edit className="h-4 w-4" />}
								label=""
							/>
							<Button
								color="error"
								size="auto"
								textOnly
								onClick={(e) => {
									e.stopPropagation();
									onDelete(instruction.id);
								}}
								startIcon={<Trash className="h-4 w-4" />}
								label=""
							/>
						</div>
					</div>
					{expandedHistoryId === instruction.id && (
						<div className="mt-2 pl-3 border-l-2 border-gray-200 ml-2">
							<VersionHistoryPanel entityId={instruction.id} contentKey="instruction_text" />
						</div>
					)}
				</div>
			))}
		</div>
	);
}
